import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import notificationService from '../services/notificationService';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const data = await notificationService.getNotifications();
      setNotifications(data.data || []);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(
        notifications.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(
        notifications.map(n => ({ ...n, is_read: true }))
      );
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await notificationService.deleteNotification(id);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleClearAll = async () => {
    if (window.confirm('Are you sure you want to clear all read notifications?')) {
      try {
        await notificationService.clearAllNotifications();
        setNotifications(notifications.filter(n => !n.is_read));
      } catch (error) {
        console.error('Failed to clear notifications:', error);
      }
    }
  };

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.is_read;
    if (filter === 'read') return n.is_read;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.is_read).length;

  const getIconByType = (type) => {
    const icons = {
      'payment_verified': '✅',
      'payment_rejected': '❌',
      'payment_due': '💰',
      'payment': '💰',
      'admission': '📝',
      'registration': '✍️',
      'agreement': '📄',
      'leave': '📅',
      'info': 'ℹ️',
      'warning': '⚠️',
      'error': '❌',
    };
    return icons[type] || '🔔';
  };

  const getColorByType = (type) => {
    const colors = {
      'payment_verified': '#10b981',
      'payment_rejected': '#ef4444',
      'payment_due': '#f59e0b',
      'payment': '#f59e0b',
      'admission': '#3b82f6',
      'registration': '#8b5cf6',
      'agreement': '#ec4899',
      'leave': '#06b6d4',
      'info': '#6b7280',
      'warning': '#f59e0b',
      'error': '#ef4444',
    };
    return colors[type] || '#84cc16';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="notifications-page">
      <style>{`
        .notifications-page {
          display: flex;
          min-height: 100vh;
          background: #f5f7fa;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .notifications-sidebar {
          width: 260px;
          background: white;
          display: flex;
          flex-direction: column;
          position: fixed;
          height: 100vh;
          border-right: 1px solid #e5e7eb;
          padding: 0;
          margin: 0;
        }

        .notifications-sidebar-header {
          padding: 24px 20px;
          border-bottom: 1px solid #e5e7eb;
          cursor: pointer;
        }

        .notifications-logo {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 0;
        }

        .notifications-logo-icon {
          width: 65px;
          height: 65px;
          object-fit: contain;
          border-radius: 8px;
        }

        .notifications-logo-text h1 {
          font-size: 18px;
          font-weight: 700;
          margin: 0;
          color: #1f2937;
        }

        .notifications-logo-text span {
          font-size: 12px;
          color: #6b7280;
          font-weight: 700;
        }

        .notifications-sidebar-nav {
          flex: 1;
          padding: 16px 12px;
          overflow-y: auto;
        }

        .notifications-sidebar-footer {
          padding: 16px 12px;
          border-top: 1px solid #e5e7eb;
        }

        .notifications-user-profile {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          padding: 10px;
          background: #f9fafb;
          border-radius: 8px;
        }

        .notifications-avatar {
          width: 36px;
          height: 36px;
          background: #84cc16;
          color: white;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
        }

        .notifications-user-info {
          display: flex;
          flex-direction: column;
        }

        .notifications-user-name {
          font-size: 13px;
          font-weight: 500;
          color: #1f2937;
        }

        .notifications-user-role {
          font-size: 11px;
          color: #6b7280;
        }

        .notifications-main {
          flex: 1;
          margin-left: 260px;
          padding: 24px 32px;
        }

        .notifications-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          padding: 20px 24px;
          background: linear-gradient(135deg, #84cc16 0%, #65a30d 50%, #4d7c0f 100%);
          border-radius: 16px;
          border: none;
          box-shadow: 
            0 10px 25px -5px rgba(101, 163, 13, 0.4),
            0 8px 16px -8px rgba(77, 124, 15, 0.3),
            0 -2px 4px 0px rgba(217, 249, 157, 0.2) inset,
            0 4px 8px -2px rgba(0, 0, 0, 0.15);
          position: relative;
        }

        .notifications-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 50%;
          background: linear-gradient(180deg, rgba(255, 255, 255, 0.15) 0%, transparent 100%);
          pointer-events: none;
        }

        .notifications-header-left h2 {
          font-size: 24px;
          font-weight: 600;
          color: white;
          margin: 0 0 4px 0;
        }

        .notifications-header-left p {
          margin: 0;
          color: white;
          font-size: 14px;
        }

        .notifications-header-right {
          display: flex;
          gap: 8px;
          z-index: 1;
          position: relative;
        }

        .notifications-btn {
          padding: 8px 14px;
          background: rgba(255, 255, 255, 0.2);
          border: 2px solid rgba(255, 255, 255, 0.3);
          color: white;
          border-radius: 8px;
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .notifications-btn:hover {
          background: rgba(255, 255, 255, 0.35);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .notifications-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .notifications-filters {
          display: flex;
          gap: 8px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .filter-btn {
          padding: 8px 16px;
          background: white;
          border: 2px solid #e5e7eb;
          color: #4b5563;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .filter-btn:hover {
          border-color: #84cc16;
          color: #84cc16;
        }

        .filter-btn.active {
          background: #84cc16;
          border-color: #84cc16;
          color: white;
        }

        .notifications-container {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          overflow: hidden;
        }

        .notifications-list {
          display: flex;
          flex-direction: column;
          divide-y divide-gray-200;
        }

        .notification-item {
          display: flex;
          gap: 12px;
          padding: 16px 20px;
          border-bottom: 1px solid #e5e7eb;
          transition: all 0.2s ease;
          cursor: pointer;
          background: white;
        }

        .notification-item:hover {
          background: #f9fafb;
        }

        .notification-item.unread {
          background: #f7fee7;
        }

        .notification-item:last-child {
          border-bottom: none;
        }

        .notification-icon {
          width: 40px;
          height: 40px;
          min-width: 40px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-title {
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .notification-unread-badge {
          width: 8px;
          height: 8px;
          background: #84cc16;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .notification-message {
          font-size: 12px;
          color: #6b7280;
          margin: 0 0 4px 0;
          line-height: 1.4;
        }

        .notification-time {
          font-size: 11px;
          color: #9ca3af;
        }

        .notification-actions {
          display: flex;
          gap: 8px;
          align-items: flex-start;
          flex-shrink: 0;
          margin-left: 12px;
        }

        .notification-action-btn {
          padding: 4px 8px;
          background: none;
          border: none;
          color: #6b7280;
          font-size: 11px;
          font-weight: 500;
          cursor: pointer;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .notification-action-btn:hover {
          background: #e5e7eb;
          color: #1f2937;
        }

        .notification-empty {
          padding: 40px 20px;
          text-align: center;
          color: #9ca3af;
          font-size: 14px;
        }

        .notifications-sidebar-btn {
          width: 100%;
          padding: 10px;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          color: #16a34a;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          margin-bottom: 8px;
        }

        .notification-back-btn {
          padding: 10px;
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          color: #1f2937;
          border-radius: 8px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          margin-bottom: 8px;
        }

        .notification-logout-btn {
          width: 100%;
          padding: 10px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
        }
      `}</style>

      <aside className="notifications-sidebar">
        <div className="notifications-sidebar-header" onClick={() => navigate('/')}>
          <div className="notifications-logo">
            <img src="/SaiNiwasPayingGuest.png" alt="Sai Nirvaar Logo" className="notifications-logo-icon" />
            <div className="notifications-logo-text">
              <h1>Sai Mess</h1>
              <span>& Hostel</span>
            </div>
          </div>
        </div>

        <div className="notifications-sidebar-nav"></div>

        <div className="notifications-sidebar-footer">
          <div className="notifications-user-profile">
            <div className="notifications-avatar">{user?.first_name?.charAt(0) || 'A'}</div>
            <div className="notifications-user-info">
              <span className="notifications-user-name">{user?.first_name || user?.username}</span>
              <span className="notifications-user-role">Administrator</span>
            </div>
          </div>
          <button className="notification-back-btn" onClick={() => navigate('/')}>← Back to Dashboard</button>
          <button className="notifications-sidebar-btn" onClick={() => navigate('/settings')} title="Settings">
            ⚙️ Settings
          </button>
          <button className="notification-logout-btn" onClick={() => {
            authService.logout();
            navigate('/login');
          }}>
            🚪 Logout
          </button>
        </div>
      </aside>

      <main className="notifications-main">
        <header className="notifications-header">
          <div className="notifications-header-left">
            <h2>Notifications</h2>
            <p>
              {notifications.length === 0
                ? 'No notifications yet'
                : `You have ${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
            </p>
          </div>
          <div className="notifications-header-right">
            {unreadCount > 0 && (
              <button className="notifications-btn" onClick={handleMarkAllAsRead}>
                Mark all as read
              </button>
            )}
            {notifications.some(n => n.is_read) && (
              <button className="notifications-btn" onClick={handleClearAll}>
                Clear read
              </button>
            )}
          </div>
        </header>

        <div className="notifications-filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All ({notifications.length})
          </button>
          <button
            className={`filter-btn ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Unread ({unreadCount})
          </button>
          <button
            className={`filter-btn ${filter === 'read' ? 'active' : ''}`}
            onClick={() => setFilter('read')}
          >
            Read ({notifications.filter(n => n.is_read).length})
          </button>
        </div>

        <div className="notifications-container">
          {loading ? (
            <div className="notification-empty">Loading notifications...</div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notification-empty">
              {notifications.length === 0
                ? 'No notifications yet. Notifications will appear here when payments are verified or rejected.'
                : filter === 'unread'
                ? 'All notifications have been read'
                : 'No read notifications'}
            </div>
          ) : (
            <div className="notifications-list">
              {filteredNotifications.map(notification => (
                <div
                  key={notification.id}
                  className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                >
                  <div
                    className="notification-icon"
                    style={{ background: getColorByType(notification.notification_type) + '20' }}
                  >
                    {getIconByType(notification.notification_type)}
                  </div>
                  <div className="notification-content" onClick={() => {
                    if (!notification.is_read) {
                      handleMarkAsRead(notification.id);
                    }
                  }}>
                    <div className="notification-title">
                      {!notification.is_read && <span className="notification-unread-badge"></span>}
                      {notification.title}
                    </div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{formatDate(notification.created_at)}</div>
                  </div>
                  <div className="notification-actions">
                    {!notification.is_read && (
                      <button
                        className="notification-action-btn"
                        onClick={() => handleMarkAsRead(notification.id)}
                      >
                        Mark read
                      </button>
                    )}
                    <button
                      className="notification-action-btn"
                      onClick={() => handleDeleteNotification(notification.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NotificationsPage;
